import { CSSProperties, useCallback, useState } from "react";
import { IBuilder } from "../../hooks/api/admin/useBuilders";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";
import { SPACING } from "../../constants/design/spacing";
import { BaseModal } from "../shared/BaseModal";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { EXCHANGE_APP_URL } from "../../utils/env";
import {
  buildHandoffBlurb,
  formatFeeRate,
  partnerLinks,
} from "../../utils/builders";

const sectionStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: SPACING.one,
};

const titleStyle: CSSProperties = {
  ...FONT_STYLE.label.two,
  color: TEXT_COLORS.two,
};

const valueStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: `${SPACING.one}px ${SPACING.two}px`,
  borderRadius: 4,
  border: `1px solid ${BORDER_COLORS.one}`,
  background: BACKGROUND_COLORS.component,
  color: TEXT_COLORS.one,
  fontFamily: "monospace",
  wordBreak: "break-all",
  whiteSpace: "pre-wrap",
};

const noteStyle: CSSProperties = {
  ...FONT_STYLE.label.three,
  color: TEXT_COLORS.two,
};

const disabledStyle: CSSProperties = {
  ...FONT_STYLE.label.two,
  color: COLORS.orange.one,
};

const modalScrollStyle: CSSProperties = {
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
};

function CopyField({ label, value }: { label: string; value: string }) {
  const { addToast } = useToast();
  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      addToast({
        type: ToastEnum.SIMPLE,
        header: `${label} copied`,
        status: ToastStatusEnum.SUCCESS,
      }, 2000);
    } catch {
      addToast({
        type: ToastEnum.SIMPLE,
        header: "Copy failed",
        subheader: "Select the text and copy it manually.",
        status: ToastStatusEnum.ERROR,
      }, 4000);
    }
  }, [addToast, label, value]);

  return (
    <div style={{ display: "flex", gap: SPACING.two, alignItems: "flex-start" }}>
      <div style={valueStyle} data-testid={`partner-${label}`}>{value}</div>
      <Button
        type="button"
        buttonTheme={ButtonThemeEnum.NEUTRAL2}
        aria-label={`Copy ${label}`}
        onClick={onCopy}
        style={{ height: 30, padding: `0 ${SPACING.two}px` }}
      >
        Copy
      </Button>
    </div>
  );
}

interface IPartnerLinksModalProps {
  builder: IBuilder;
  cap: string | null | undefined;
  show: boolean;
  onHide: () => void;
  // Title override, e.g. right after a successful create.
  title?: string;
}

export function PartnerLinksModal({
  builder, cap, show, onHide, title,
}: IPartnerLinksModalProps) {
  // Default the approval rate to the current protocol cap.
  const [maxFeeRate, setMaxFeeRate] = useState(cap ?? "");
  const links = partnerLinks({
    appUrl: EXCHANGE_APP_URL(),
    builderId: builder.builder_id,
    status: builder.status,
    cap,
    maxFeeRate,
  });
  const blurb = buildHandoffBlurb({
    builderId: builder.builder_id,
    feeAccount: builder.fee_account,
    approvalLink: links.approvalLink,
    dashboardLink: links.dashboardLink,
  });
  const rateEditable = builder.status === "ACTIVE" && cap !== null && cap !== undefined;

  return (
    <BaseModal
      show={show}
      onHide={onHide}
      title={title ?? `Partner links — ${builder.name}`}
      style={{ ...modalScrollStyle, width: 560, minWidth: 560 }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: SPACING.three }}>
        <div style={sectionStyle}>
          <span style={titleStyle}>Builder ID</span>
          <CopyField label="builder_id" value={builder.builder_id} />
        </div>

        <div style={sectionStyle}>
          <span style={titleStyle}>Approval link (send users here)</span>
          {rateEditable && (
            <label htmlFor="partner-max-fee-rate" style={noteStyle}>
              max_fee_rate (≤ protocol cap {formatFeeRate(cap as string)})
              <input
                id="partner-max-fee-rate"
                value={maxFeeRate}
                onChange={(e) => setMaxFeeRate(e.target.value.trim())}
                style={{ ...valueStyle, display: "block", width: 200, marginTop: SPACING.one }}
              />
            </label>
          )}
          {links.approvalLink ? (
            <>
              <CopyField label="approval link" value={links.approvalLink} />
              <span style={noteStyle}>
                Users approve up to {formatFeeRate(maxFeeRate)}; the partner may charge any per-order rate up to that.
              </span>
            </>
          ) : (
            <span role="status" style={disabledStyle}>{links.approvalDisabledReason}</span>
          )}
        </div>

        <div style={sectionStyle}>
          <span style={titleStyle}>Builder dashboard</span>
          {links.dashboardLink ? (
            <>
              <CopyField label="dashboard link" value={links.dashboardLink} />
              <span style={noteStyle}>Log in with the fee account {builder.fee_account}</span>
            </>
          ) : (
            <span style={disabledStyle}>{links.dashboardDisabledReason}</span>
          )}
        </div>

        <div style={sectionStyle}>
          <span style={titleStyle}>Handoff message</span>
          <CopyField label="handoff message" value={blurb} />
        </div>
      </div>
    </BaseModal>
  );
}
